/*
 * Message document in, markdown out. Markdown is the only persisted form of a message — the draft,
 * the outbox entry, the edit and the wire all carry it — so this file runs on every keystroke and
 * its output is what the server parses.
 *
 * Only the writers a message answers differently stand here; a paragraph, a quote, a rule, an image
 * and the four inline markers are the builder's (`../dialect/serialize.ts`), because nothing about
 * them is a message's decision.
 *
 * Two rules are worth knowing before reading a writer below. A single newline is a line break in
 * this dialect, so `hard_break` writes one and re-arms the line-start escaper for what follows. And
 * an ampersand that would open a character reference is escaped: the server resolves `&amp;`, so
 * text that reads "&amp;" has to leave here as `\&amp;` or the message would change on the way.
 */

import { Fragment, type Node, type Schema } from "prosemirror-model"
import {
	fenceFor,
	nodeType,
	sharedMarkWriters,
	sharedNodeWriters,
	withoutBreaks,
	type MarkWriter,
	type NodeWriter,
	type SerializerSpec,
	type WritableState,
} from "../dialect/index.ts"

/** An `&` that would open a character reference the server resolves. */
const ENTITY_START = /&(?=[a-zA-Z][a-zA-Z0-9]*;|#[0-9]{1,7};|#[xX][0-9a-fA-F]{1,6};)/g

/** The `[ ] ` or `[x] ` a task item opens with; an ordinary item opens with nothing. */
function taskPrefix(item: Node): string {
	const checked = item.attrs.checked as boolean | null
	if (checked === null) return ""
	return checked ? "[x] " : "[ ] "
}

/**
 * An autolink is written bare: the server linkifies a URL with a scheme and a bare address alike, so
 * `https://x` and `anna@example.org` come back as the same link they went in as. It is only safe
 * while the visible text *is* the destination — after an edit it may not be, and then the link falls
 * back to the ordinary `[text](href)` form rather than losing the href.
 */
function isPlainAutolink(node: Node, href: string, auto: boolean): boolean {
	if (!auto || !node.isText || node.marks.length !== 1) return false
	const text = node.text ?? ""
	return text === href || `mailto:${text}` === href
}

const ALIGNMENT: Record<string, string> = {
	"": "---",
	left: ":---",
	center: ":---:",
	right: "---:",
}

/**
 * The message's writers. `write` is the dialect's own serializer, which one table cell needs: a
 * cell is rendered through a document of one paragraph so that the same node and mark writers run
 * over it — a cell keeps its `**bold**` — and the column separator is escaped afterwards.
 */
export function messageWriters(schema: Schema, write: (doc: Node) => string): SerializerSpec {
	const hardBreak = nodeType(schema, "message", "hard_break")
	const paragraph = nodeType(schema, "message", "paragraph")

	const oneLine = (node: Node): Node => withoutBreaks(node, hardBreak, schema)

	/** One table cell's inline content as markdown, on the single line a row allows. */
	function cellMarkdown(cell: Node): string {
		const line = paragraph.create(null, oneLine(cell).content)
		const doc = schema.topNodeType.create(null, Fragment.from(line))
		return write(doc).replace(/\n+/g, " ").replaceAll("|", "\\|").trim()
	}

	const nodes: Record<string, NodeWriter> = {
		...sharedNodeWriters(),

		heading(state, node) {
			state.write(`${"#".repeat(node.attrs.level as number)} `)
			state.renderInline(oneLine(node))
			state.closeBlock(node)
		},

		code_block(state, node) {
			const text = node.textContent
			const fence = fenceFor(text)
			state.write(`${fence}${node.attrs.params as string}\n`)
			state.text(text, false)
			state.ensureNewLine()
			state.write(fence)
			state.closeBlock(node)
		},

		bullet_list(state, node) {
			state.renderList(node, "  ", (i) => `- ${taskPrefix(node.child(i))}`)
		},

		ordered_list(state, node) {
			const start = (node.attrs.order as number) || 1
			const width = String(start + node.childCount - 1).length
			state.renderList(node, " ".repeat(width + 2), (i) => {
				const number = String(start + i)
				return `${" ".repeat(width - number.length)}${number}. ${taskPrefix(node.child(i))}`
			})
		},

		table(state, node) {
			const header = node.firstChild
			if (header === null) return
			const rows: string[] = []
			node.forEach((row) => {
				const cells: string[] = []
				row.forEach((cell) => cells.push(cellMarkdown(cell)))
				rows.push(`| ${cells.join(" | ")} |`)
			})
			const alignments: string[] = []
			header.forEach((cell) => alignments.push(ALIGNMENT[cell.attrs.align as string] ?? "---"))
			rows.splice(1, 0, `| ${alignments.join(" | ")} |`)
			state.write(rows.join("\n"))
			state.closeBlock(node)
		},

		hard_break(state) {
			const writable = state as WritableState
			writable.out += "\n"
		},

		text(state, node, parent, index) {
			const writable = state as WritableState
			// A line that opens after a break is a line start for markdown too: without this, `- ` on
			// the second line of a paragraph would come back as a list.
			if (index > 0 && parent.child(index - 1).type === hardBreak) {
				writable.atBlockStart = true
			}
			state.text(node.text ?? "", writable.inAutolink !== true)
		},
	}

	const marks: Record<string, MarkWriter> = {
		...sharedMarkWriters(),
		link: {
			open(state, mark, parent, index) {
				const writable = state as WritableState
				writable.inAutolink = isPlainAutolink(
					parent.child(index),
					mark.attrs.href as string,
					mark.attrs.auto as boolean,
				)
				return writable.inAutolink ? "" : "["
			},
			close(state, mark) {
				const writable = state as WritableState
				const auto = writable.inAutolink
				writable.inAutolink = undefined
				if (auto === true) return ""
				const href = (mark.attrs.href as string).replace(/[()"]/g, "\\$&")
				const title = mark.attrs.title as string
				return `](${href}${title === "" ? "" : ` "${title.replaceAll('"', '\\"')}"`})`
			},
			mixable: true,
		},
	}

	return {
		nodes,
		marks,
		options: { escapeExtraCharacters: ENTITY_START },
		serializeOptions: { tightLists: true },
	}
}
